import { useRef } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useFirstEventAccess } from "@/hooks/useFirstEventAccess";

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
  </div>
);

/**
 * /onboarding: libera em "full" e "criar". Em "leitura", vai para /planos.
 */
export function FirstEventGate({ children }: { children: React.ReactNode }) {
  const { state, loading } = useFirstEventAccess();
  // Congela a decisão da montagem: se o cronograma for gerado durante a visita,
  // a pessoa não é expulsa da tela no meio do caminho.
  const grantedRef = useRef(false);

  if (grantedRef.current) return <>{children}</>;
  if (loading) return <Spinner />;
  if (state !== "full" && state !== "criar") {
    return <Navigate to="/planos" replace state={{ blocked: true }} />;
  }

  grantedRef.current = true;
  return <>{children}</>;
}

/** Telas do primeiro evento liberadas sem assinatura (relativas a /evento/:id). */
export const FIRST_EVENT_ALLOWED_PATHS = [
  "",
  "dashboard",
  "detalhes",
  "briefing",
  "lista-tarefas",
  "tarefas",
  "tarefas-hoje",
  "tarefas-semana",
  "cronograma",
];

/**
 * /evento/:id/*: "full" passa sempre. "criar" e "leitura" só no PRIMEIRO evento e
 * só nos caminhos de FIRST_EVENT_ALLOWED_PATHS.
 */
export function FirstEventRouteGate({ children }: { children: React.ReactNode }) {
  const { state, firstEventId, loading } = useFirstEventAccess();
  const { id } = useParams();
  const location = useLocation();
  // Congela o acesso concedido: a tela recém-gerada não some no meio do caminho.
  const grantedRef = useRef(false);
  if (!loading) {
    grantedRef.current = true;
  }

  if (loading && !grantedRef.current) return <Spinner />;

  if (state !== "full") {
    const sub = location.pathname
      .split(`/evento/${id}`)[1]
      ?.replace(/^\//, "")
      .replace(/\/$/, "") ?? "";
    const pathAllowed = FIRST_EVENT_ALLOWED_PATHS.includes(sub);
    const isFirstEvent = !!firstEventId && firstEventId === id;
    if (!grantedRef.current || !pathAllowed || !isFirstEvent) {
      return <Navigate to="/planos" replace state={{ blocked: true }} />;
    }
  }

  return <>{children}</>;
}


export default FirstEventGate;
